import { Selection, EnterElement } from "d3"
import { D3VisualizationRenderer } from "../../D3VisualizationRenderer"
import { D3ClipPath } from "../../../D3/D3ClipPath"
import { CPPOptPlotConfig } from "../../../../Types/CPPOptPlot"
import { throttledRender } from "../../../../rendering"
import { D3Timeline } from "../../../D3/Timeline/D3Timeline"
import { D3HistogramXAxis } from "../../Histogram/D3/D3HistogramXAxis"
import { D3ValueAxis } from "../../../D3/D3ValueAxis"
import { D3HorizontalLines } from "../../../D3/D3HorizontalLines"
import { D3Parabola } from "../../../D3/D3Parabola"
import { D3Point } from "../../../D3/D3Point"
import { LEFT_MARGIN } from "../../Constants"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { MobergAnimationTimingMs } from "../../../../../../../../Moberg"
import { DataSource } from "../../../../Types/DataSource"
import { CompositeTimeSeriesData, findMostRelevantCompositeRow } from "../../../../Data/TimeSeriesData"
import { D3MAPOptPlot } from "./D3MAPOptPlot"
import { D3MAPOptPlotConfigurationBuilder } from "./D3MAPOptConfigurationBuilder"
import { D3ErrorBars } from "../../CPPOpt/D3/D3CPPOptErrorBars"

export class D3MAPOptPlotRenderer extends D3VisualizationRenderer<D3MAPOptPlot, D3MAPOptPlotConfigurationBuilder> {
	// CHILDREN
	private clipPath?: D3ClipPath
	private xAxis?: D3HistogramXAxis
	private yAxis?: D3ValueAxis
	private horizontalLines?: D3HorizontalLines
	private parabola?: D3Parabola
	private errorBars?: D3ErrorBars
	private minimumPoint?: D3Point
	private llaPoint?: D3Point
	private ulaPoint?: D3Point

	public onYAxisDragStart(): void {
		this.configBuilder.transitionDuration = 0
		this.updateChildren()
	}

	public onYAxisDrag(): void {
		this.horizontalLines?.render()
		this.parabola?.render()
		this.errorBars?.render()
		this.minimumPoint?.render()
		this.llaPoint?.render()
		this.ulaPoint?.render()
	}

	public onYAxisDragEnd(): void {
		this.configBuilder.transitionDuration = MobergAnimationTimingMs.SLOW
		this.updateChildren()
	}

	public viewTimesChanged(): void {
		this.timeline?.viewTimesChanged()
		this.updateMAPOptData()
	}

	public onTimelineSliderDrag(): void {
		this.updateMAPOptData()
	}

	public renderPage(page: ModalityPage) {
		this.updateMAPOptData()
	}

	protected canRender(): boolean {
		return this.visualization.boundingBox.height > 0 && this.visualization.boundingBox.width > 0
	}

	protected enter(enterElements: Selection<EnterElement, any, any, any>): Selection<any, any, any, any> {
		this.updateMAPOptData()
		
		const flexContainer = enterElements
			.append("div")
			.attr("class", this.className)
			.style("display", "flex")
			.style("flex-direction", "column")

			// SVGs are absolutely positioned. To mimic this behavior and let CSS grid handle the layout,
			// we have to absolutely position this div to only let it take up only the room we give it.
			.style("z-index", 1)
			.style("position", "absolute")
			.style("top", 0)
			.style("left", 0)
			.style("width", "100%")
			.style("height", "100%")

		// Create the plot container
		const plotContainer = flexContainer
			.append("div")
			.style("flex", 1)
			.style("min-height", 0)

		const upperSvg = plotContainer
			.append("svg")
			.style("display", "block")
			.attr("width", "100%")
			.attr("height", "100%")

		const upperBoundingBox = upperSvg
			.append("g")
			.attr("transform", `translate(${this.visualization.boundingBox.x}, ${this.visualization.boundingBox.y})`)

		// Create the timeline container
		const timelineFlexContainer = flexContainer
			.append("div")
			.style("flex", 0)
			.style("min-height", "90px")

		const timelineSvg = timelineFlexContainer
			.append("svg")
			.attr("class", "lower-svg")
			.attr("width", "100%")
			.attr("height", "100%")

		const timelineContainer = timelineSvg
			.append("g")
			.attr("transform", `translate(${LEFT_MARGIN}, 32)`)

		upperBoundingBox.each((config, index, nodes) => this.createUpperBoundingBoxChildren(config, index, nodes))
		timelineContainer.each((config, index, nodes) => this.createTimeline(config, index, nodes))
		upperSvg.each((config, index, nodes) => this.createClipPath(config, index, nodes))

		this.renderChildren()

		return flexContainer
	}

	private createUpperBoundingBoxChildren(config: CPPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
		const root = nodes[index]

		this.yAxis = new D3ValueAxis(root, this.configBuilder.getYAxisConfig(), this.visualization.reactCallbacks)
		this.xAxis = new D3HistogramXAxis(root, this.configBuilder.getXAxisConfig(), this.visualization.reactCallbacks)
		this.horizontalLines = new D3HorizontalLines(root, this.configBuilder.getHorizontalLinesConfig())
		this.parabola = new D3Parabola(root, this.configBuilder.getParabolaConfig(), this.visualization.reactCallbacks)
		this.errorBars = new D3ErrorBars(root, this.configBuilder.getErrorBarsConfig(), this.visualization.reactCallbacks)

		const cppOpt = this.configBuilder.mapOptData.MAPOpt ?? 0
		this.minimumPoint = new D3Point(root, this.configBuilder.getMinimumPointConfig(cppOpt, this.parabola.evaluate(cppOpt)), "d3-minimum-point", this.visualization.reactCallbacks)

		const LLA = this.configBuilder.mapOptData.LLA ?? 0
		this.llaPoint = new D3Point(root, this.configBuilder.getLLAPointConfig(LLA, this.parabola.evaluate(LLA)), "d3-lla-point", this.visualization.reactCallbacks)

		const ULA = this.configBuilder.mapOptData.ULA ?? 0
		this.ulaPoint = new D3Point(root, this.configBuilder.getULAPointConfig(ULA, this.parabola.evaluate(ULA)), "d3-ula-point", this.visualization.reactCallbacks)
	}

	private createClipPath(config: CPPOptPlotConfig, index: number, nodes: ArrayLike<SVGSVGElement>) {
		const root = nodes[index]
		this.clipPath = new D3ClipPath(root, this.configBuilder.getClipPathConfig(), this.visualization.reactCallbacks)
	}

	protected update = (updatedElements: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const flexContainer = updatedElements

		this.renderChildren()

		return flexContainer
	}

	private createTimeline = (config: CPPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.timeline = new D3Timeline(root, this.configBuilder.getTimelineConfig(), this.visualization.timeSeriesPageManager, this.visualization.reactCallbacks)
	}

	private renderChildren = () => {
		this.yAxis?.render()
		this.xAxis?.render()
		this.timeline?.render()
		this.horizontalLines?.render()
		this.parabola?.render()
		this.errorBars?.render()
		this.minimumPoint?.render()
		this.llaPoint?.render()
		this.ulaPoint?.render()
	}

	public updateChildren = () => {
		this.yAxis?.updateConfig(this.configBuilder.getYAxisConfig())
		this.xAxis?.updateConfig(this.configBuilder.getXAxisConfig())
		this.timeline?.updateConfig(this.configBuilder.getTimelineConfig())
		this.clipPath?.updateConfig(this.configBuilder.getClipPathConfig())
		this.parabola?.updateConfig(this.configBuilder.getParabolaConfig())
		this.horizontalLines?.updateConfig(this.configBuilder.getHorizontalLinesConfig())
		this.errorBars?.updateConfig(this.configBuilder.getErrorBarsConfig())

		const cppOpt = this.configBuilder.mapOptData.MAPOpt ?? 0
		this.minimumPoint?.updateConfig(this.configBuilder.getMinimumPointConfig(cppOpt, this.parabola?.evaluate(cppOpt) ?? 0))

		const LLA = this.configBuilder.mapOptData.LLA ?? 0
		this.llaPoint?.updateConfig(this.configBuilder.getLLAPointConfig(LLA, this.parabola?.evaluate(LLA) ?? 0))

		const ULA = this.configBuilder.mapOptData.ULA ?? 0
		this.ulaPoint?.updateConfig(this.configBuilder.getLLAPointConfig(ULA, this.parabola?.evaluate(ULA) ?? 0))
	}

	private updateMAPOptData = throttledRender(() => {
		const [currentPage, latestPage] = this.visualization.timeSeriesPageManager.getPagesInView()
		const dataObjectId = this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number

		// Go through each of the pages, and try to find the most relevant data point
		let mostRelevantIndex = -1
		let compositeData: CompositeTimeSeriesData | undefined = undefined

		for (const page of [latestPage, currentPage]) {
			compositeData = page?.data.get(dataObjectId)?.get("MAPOpt,Composite,SampleSeries") as CompositeTimeSeriesData | undefined

			if (!compositeData) {
				continue
			}

			const mostRelevantIndexCandidate = findMostRelevantCompositeRow(compositeData, this.visualization.config.viewScale.domain()[1].getTime())
			
			if (mostRelevantIndexCandidate > -1) {
				mostRelevantIndex = mostRelevantIndexCandidate
				break
			}
		}

		if (!compositeData) {
			return
		}

		const latestCompositeData = new Array(compositeData.data.length).fill(null)
		const numBins = this.visualization.bins.length

		if (mostRelevantIndex > -1) {
			for (let i = 0; i < compositeData.data.length; i++) {
				latestCompositeData[i] = compositeData.data[i][mostRelevantIndex]
			}
		}

		this.configBuilder.updateMAPOptData({
			MAPOpt: latestCompositeData[0],
			LLA: latestCompositeData[1],
			ULA: latestCompositeData[2],
			fitType: latestCompositeData[3],
			fitSpan: latestCompositeData[4],
			includedDataPercentage: latestCompositeData[5],
			coefficients: latestCompositeData.slice(6, 9),
			yValues: latestCompositeData.slice(9, 8 + numBins),
			errors: latestCompositeData.slice(8 + numBins, 7 + 2*numBins),
			dataPercentages: latestCompositeData.slice(7 + 2*numBins, 6 + 3*numBins),
			includedInFit: latestCompositeData.slice(6 + 3*numBins, 5 + 4*numBins),
			weight: null
		})

		this.updateChildren()
	}, this.configBuilder.transitionDuration)
}
